import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, Mail } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error("Envoi impossible. Vérifiez votre email et réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="contact-page">
      <Seo
        title="Contact — Gestimmony"
        description="Une question sur la gestion locative avec Gestimmony ? Écrivez-nous, nous répondons rapidement."
        path="/contact"
      />
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
            <LogoMark className="h-8" />
            <span className="font-heading font-bold">Gestimmony</span>
          </Link>
          <Link to="/tarifs" className="text-sm text-slate-600 hover:text-[hsl(var(--primary))]">Tarifs</Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-12">
        <div className="inline-flex items-center gap-2 rounded-full bg-teal-100 text-teal-800 px-3 py-1 text-xs font-semibold mb-4">
          <Mail className="h-3.5 w-3.5" /> Contact
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Une question ? Écrivez-nous</h1>
        <p className="mt-3 text-slate-600">Nous répondons généralement sous 24 h ouvrées.</p>

        {sent ? (
          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="contact-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <div className="mt-3 font-heading font-bold text-lg text-slate-900">Message envoyé</div>
            <p className="mt-1 text-sm text-slate-600">Merci, nous revenons vers vous très vite.</p>
            <Link to="/" className="mt-4 inline-block text-sm font-semibold text-[hsl(var(--primary))]">← Retour à l'accueil</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 bg-white rounded-2xl border border-stone-200 p-6">
            <div>
              <Label>Nom complet</Label>
              <Input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Sujet <span className="text-slate-400">(optionnel)</span></Label>
              <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <Button type="submit" disabled={busy} data-testid="contact-submit" className="w-full bg-[hsl(var(--primary))]">
              {busy ? "Envoi…" : "Envoyer le message"}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
